import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Skovkortet assets
import skovkortetLogo from "assets/images/branding/skovkortet/skovkortet-logo.png";
import skovkortetLogoIcon from "assets/images/branding/skovkortet/skovkortet-logo-icon.png";

// Forsler assets
import forslerLogo from "assets/images/branding/forsler/forsler-logo.png";
import forslerLogoIcon from "assets/images/branding/forsler/forsler-logo-icon.png";

// George assets
import georgeLogo from "assets/images/branding/george/george-logo.svg";
import georgeLogoIcon from "assets/images/branding/george/george-logo-icon.svg";
import georgeFavicon from "assets/images/branding/george/george-favicon.png";

import {
  ApplyBrandingPayload,
  BrandingPackageSchemaType,
  BrandingScope,
  BrandingState,
  BrandingStatus,
  InternalAppType,
  UiBranding,
} from "types/branding";

export const HARD_DEFAULT_GEORGE_UI: UiBranding = {
  appName: "George",
  shortName: "George",
  tagline: "",
  primaryColor: "#2E7D32",
  secondaryColor: "#1565C0",
  preferredLogoVariant: "default",
  assets: {
    logo: { url: georgeLogoIcon },
    favicon: { url: georgeFavicon },
    printHeaderLogo: { url: georgeLogo },
  },
  legal: {
    companyName: "Forsler ApS",
    address: "Østre Allé 102, 9000 Aalborg, Denmark",
    vatId: "40417664",
  },
  links: {
    websiteUrl: "https://www.georgemaps.io",
    termsUrl: "https://www.georgemaps.io/terms-of-use-and-conditions",
    privacyUrl: "https://www.georgemaps.io/cookie-and-privacy-policy",
    aboutUrl: "https://www.georgemaps.io/our-world/about-us",
  },
};

export const HARD_DEFAULT_FORSLER_UI: UiBranding = {
  appName: "Forsler.com",
  shortName: "Forsler",
  tagline: "",
  primaryColor: "#2E7D32",
  secondaryColor: "#1565C0",
  preferredLogoVariant: "default",
  assets: {
    logo: { url: forslerLogoIcon },
    favicon: { url: `${process.env.PUBLIC_URL || ""}/favicon-forsler.ico` },
    printHeaderLogo: { url: forslerLogo },
  },
  legal: {
    companyName: "Forsler ApS",
    address: "Østre Allé 102, 9000 Aalborg, Denmark",
    vatId: "40417664",
  },
  links: {
    websiteUrl: "https://www.forsler.com",
    termsUrl: "https://forsler.com/terms-of-use-and-conditions",
    privacyUrl: "https://forsler.com/cookie-and-privacy-policy",
    aboutUrl: "https://forsler.com/our-world/about-us",
  },
};

export const HARD_DEFAULT_SKOVKORTET_UI: UiBranding = {
  appName: "Skovkortet.dk",
  shortName: "Skovkortet",
  tagline: "",
  primaryColor: "#2E7D32",
  secondaryColor: "#1565C0",
  preferredLogoVariant: "default",
  assets: {
    logo: { url: skovkortetLogoIcon },
    favicon: { url: `${process.env.PUBLIC_URL || ""}/favicon-forsler.ico` },
    printHeaderLogo: { url: skovkortetLogo },
  },
  legal: {
    companyName: "Forsler ApS",
    address: "Østre Allé 102, 9000 Aalborg, Danmark",
    vatId: "40417664",
  },
  links: {
    websiteUrl: "https://www.skovkortet.dk",
    termsUrl: "https://skovkortet.dk/brugervilkar-og-betingelser",
    privacyUrl: "https://skovkortet.dk/policy",
    aboutUrl: "https://skovkortet.dk/vores-verden/om-os",
  },
};

const scopePriority: Record<BrandingScope, number> = {
  default: 0,
  url: 1,
  org: 2,
  sub: 3,
};

const initialState: BrandingState = {
  scope: "default",
  status: "ready",
  brandingPackageId: null,
  ui: HARD_DEFAULT_GEORGE_UI,
  defaultLogoIconUrl: georgeLogoIcon,
  defaultPrintHeaderLogoUrl: georgeLogo, // using logo icon as fallback for print header logo, since it's more likely to be present in branding packages and still looks acceptable when scaled up for print
  raw: null,
};

export const brandingSlice = createSlice({
  name: "branding",
  initialState,
  reducers: {
    setStatus(state, action: PayloadAction<{ status: BrandingStatus; error?: string }>) {
      state.status = action.payload.status;
      state.error = action.payload.error;
    },

    // Apply normalized branding, with precedence protection
    applyBranding(state, action: PayloadAction<ApplyBrandingPayload>) {
      const incoming = action.payload.scope;
      if (scopePriority[incoming] < scopePriority[state.scope]) return;
      state.scope = incoming;
      state.brandingPackageId = action.payload.brandingPackageId;
      state.raw = action.payload.raw;
      state.ui = normalize(action.payload.raw, action.payload.internalAppType);
      state.error = undefined;
      state.status = "ready";
    },

    resetToDefault(state, action: PayloadAction<{ internalAppType: InternalAppType }>) {
      // The branding coordinator resets before applying the current winning package.
      state.scope = "default";
      state.brandingPackageId = null;
      state.raw = null;
      state.ui = HARD_DEFAULT_GEORGE_UI;
      state.defaultLogoIconUrl = georgeLogoIcon;
      state.defaultPrintHeaderLogoUrl = georgeLogo;
      if (action.payload.internalAppType === "skovkortet") {
        state.ui = HARD_DEFAULT_SKOVKORTET_UI;
        state.defaultLogoIconUrl = skovkortetLogoIcon;
        state.defaultPrintHeaderLogoUrl = skovkortetLogo;
      } else if (action.payload.internalAppType === "forsler") {
        state.ui = HARD_DEFAULT_FORSLER_UI;
        state.defaultLogoIconUrl = forslerLogoIcon;
        state.defaultPrintHeaderLogoUrl = forslerLogo;
      }
      state.status = "ready";
      state.error = undefined;
    },
  },
});

export const { applyBranding, setStatus, resetToDefault } = brandingSlice.actions;
export default brandingSlice.reducer;

// --- Selectors --- //
export const selectBranding = (s: any) => s.branding as BrandingState;
export const selectUiBranding = (s: any) => (s.branding as BrandingState).ui;
export const selectDefaultAssets = (s: any) => ({
  logo: (s.branding as BrandingState).defaultLogoIconUrl,
  printHeaderLogo: (s.branding as BrandingState).defaultPrintHeaderLogoUrl,
});
export const selectBrandingStatus = (s: any) => (s.branding as BrandingState).status;
export const selectBrandingScope = (s: any) => (s.branding as BrandingState).scope;
export const selectBrandingError = (s: any) => (s.branding as BrandingState).error;
export const selectBrandingLinks = (s: any) => (s.branding as BrandingState).ui.links;

// --- Utils --- //
function normalize(
  pkg: BrandingPackageSchemaType | null,
  internalAppType?: InternalAppType,
): UiBranding {
  let defaultUi = HARD_DEFAULT_GEORGE_UI;
  if (internalAppType === "skovkortet") defaultUi = HARD_DEFAULT_SKOVKORTET_UI;
  if (internalAppType === "forsler") defaultUi = HARD_DEFAULT_FORSLER_UI;
  if (!pkg) return defaultUi;

  return {
    ...defaultUi,
    appName:
      pkg.display?.appName && pkg.display?.appName !== "" ? pkg.display.appName : defaultUi.appName,
    shortName:
      pkg.display?.shortName && pkg.display?.shortName !== ""
        ? pkg.display.shortName
        : defaultUi.shortName,
    tagline:
      pkg.display?.tagline && pkg.display?.tagline !== "" ? pkg.display.tagline : defaultUi.tagline,
    primaryColor: pkg.theme?.primaryColor ?? defaultUi.primaryColor,
    secondaryColor: pkg.theme?.secondaryColor ?? defaultUi.secondaryColor,
    preferredLogoVariant: pkg.theme?.preferredLogoVariant ?? defaultUi.preferredLogoVariant,
    links: {
      termsUrl: pkg.links?.termsUrl ?? defaultUi.links?.termsUrl,
      privacyUrl: pkg.links?.privacyUrl ?? defaultUi.links?.privacyUrl,
      supportUrl: pkg.links?.supportUrl ?? defaultUi.links?.supportUrl,
      websiteUrl: pkg.links?.websiteUrl ?? defaultUi.links?.websiteUrl,
      aboutUrl: pkg.links?.aboutUrl ?? defaultUi.links?.aboutUrl,
    },
    legal: {
      companyName: pkg.legal?.companyName ?? defaultUi.legal?.companyName,
      address: pkg.legal?.address ?? defaultUi.legal?.address,
      vatId: pkg.legal?.vatId ?? defaultUi.legal?.vatId,
    },
    runtimeHints: pkg.runtimeHints ?? defaultUi.runtimeHints,
    assets: {
      logo: pkg.assets?.logo ?? defaultUi.assets.logo,
      logoLight: pkg.assets?.logoLight ?? defaultUi.assets.logoLight,
      logoDark: pkg.assets?.logoDark ?? defaultUi.assets.logoDark,
      favicon: pkg.assets?.favicon ?? defaultUi.assets.favicon,
      printHeaderLogo: pkg.assets?.printHeaderLogo ?? defaultUi.assets.printHeaderLogo,
    },
    version: pkg.version,
    lastModifiedDate: pkg.lastModifiedDate,
  };
}
